/**
 * C3 (round 8) -- arrows in catalogue strings point the reader's way.
 *
 * `<Arrow>` handles arrows written in JSX, and its docstring used to
 * say a catalogue arrow was content: "a translator writing the Arabic
 * writes the arrow that belongs with it." That was wrong twice over.
 *
 * It is wrong in principle, because a translator meeting "<- Back to
 * assessment grid" cannot fix the direction without also being told
 * the convention -- the arrow is inside the string they are asked to
 * translate, and nothing on the screen tells them it is a direction
 * rather than a word.
 *
 * It is wrong in practice, because the result is the product
 * contradicting itself one click apart: the cycle page's back link
 * mirrors correctly through `<Arrow>` and the analyser's, rendered
 * from the catalogue, points the other way on the same journey. Eighth
 * round for the family -- the board card's ->, the provenance
 * lineage's vendor->service->ibs->channel, the war room's "cut RTO 4h
 * -> 2h", readiness' "IBS -> service dependency edges".
 *
 * THE CONVENTION, and it is the whole contract: arrows in catalogue
 * strings are written in LEFT-TO-RIGHT form. Forward is "->", back is
 * "<-". A reader in an RTL language sees them mirrored. A translator
 * does not have to know this -- leaving the arrow exactly where the
 * English had it is correct, which is less to think about than before,
 * not more.
 *
 * Both the real glyphs and their ASCII spellings are handled, because
 * the catalogue carries both and a reader cannot be expected to care
 * which one somebody typed.
 */

const MIRROR: Record<string, string> = {
  "→": "←", // → ←
  "←": "→", // ← →
  "⇒": "⇐", // ⇒ ⇐
  "⇐": "⇒", // ⇐ ⇒
  "↗": "↖", // ↗ ↖
  "↖": "↗", // ↖ ↗
  "->": "<-",
  "<-": "->",
};

// Longest first, so "<-" is never matched as "-" plus something.
const PATTERN = /->|<-|[→←⇒⇐↗↖]/g;

/**
 * Mirror every arrow in a rendered string. A no-op for LTR readers, so
 * the English is untouched and the mechanism costs nothing there.
 */
export function mirrorArrows(text: string, rtl: boolean): string {
  if (!rtl || !text) return text;
  return text.replace(PATTERN, (m) => MIRROR[m] ?? m);
}
